import { useState, type ReactNode } from 'react';
import { ChevronDown } from '@ui/shared/icons';

/**
 * Shared collapsed-by-default card shell for Phase 7's six new phone
 * content cards (today-phone-parity.md §2). One wrapper renders the
 * consistent chrome (collapsed row, divider, expand/collapse toggle); each
 * card supplies only its own body content — never six bespoke re-
 * implementations of the same row.
 *
 * Fixed collapsed-row height (~56-60px, matching `RuledCheckRow`'s existing
 * precedent) is achieved by NEVER wrapping the title/summary to a second
 * line (§2.1's binding "one line only, ellipsis on overflow, no exceptions"
 * rule) — six collapsed cards therefore add a small, predictable, bounded
 * amount of page height regardless of how much content any card holds once
 * expanded (§6's whole-page height accounting depends on this).
 *
 * `isLast` controls the bottom divider (mirrors `RuledCheckRow`'s own
 * top-border-always/bottom-border-only-on-last-row convention so the
 * six-card stack reads as one coherent ruled block).
 */
export function CollapsibleCard({
  title,
  summary,
  isLast,
  children,
}: {
  readonly title: string;
  /** One-line ambient summary (e.g. "Priorities · 2 of 5 done"); omitted
   *  entirely for an untouched card per §4.1's "no card's empty-state
   *  summary uses a count" rule. */
  readonly summary?: string;
  readonly isLast: boolean;
  readonly children: ReactNode;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        borderTop: '1px solid var(--border-subtle)',
        borderBottom: isLast ? '1px solid var(--border-subtle)' : 'none',
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="collapsible-card-row"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
          width: '100%',
          minHeight: 'var(--touch-target-min)',
          padding: 'var(--space-3) var(--space-4)',
          textAlign: 'left',
          background: 'transparent',
        }}
      >
        <span style={{ minWidth: 0, flex: '1 1 auto', display: 'flex', flexDirection: 'column' }}>
          <span
            style={{
              fontSize: 'var(--type-body-size)',
              fontWeight: 500,
              color: 'var(--text-primary)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </span>
          {summary && (
            <span
              style={{
                marginTop: '2px',
                fontSize: 'var(--type-caption-size)',
                color: 'var(--text-muted)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {summary}
            </span>
          )}
        </span>
        <span
          aria-hidden="true"
          style={{
            flex: 'none',
            display: 'flex',
            color: 'var(--text-muted)',
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform var(--dur-fast) var(--ease-standard)',
          }}
        >
          <ChevronDown size={20} aria-hidden="true" />
        </span>
      </button>
      {open && (
        <div style={{ padding: '0 var(--space-4) var(--space-4)' }}>{children}</div>
      )}
    </div>
  );
}
